import React, { useState } from 'react';
import jsPDF from 'jspdf';
import { readExcelFromBase64 } from '../../services/excelService';
import { norm, sigWords, buildComparison } from '../../utils/parkComparison';

const API = process.env.REACT_APP_API_BASE_URL || '';

const tr = (s) => {
  if (!s) return '';
  return String(s)
    .replace(/ç/g,'c').replace(/Ç/g,'C')
    .replace(/ğ/g,'g').replace(/Ğ/g,'G')
    .replace(/ı/g,'i').replace(/İ/g,'I')
    .replace(/ö/g,'o').replace(/Ö/g,'O')
    .replace(/ş/g,'s').replace(/Ş/g,'S')
    .replace(/ü/g,'u').replace(/Ü/g,'U');
};
const fmtNum = (n) => n == null ? '-' : parseFloat(n).toLocaleString('tr-TR', { maximumFractionDigits: 1 });

const DataInventoryReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedExDb, setExpandedExDb] = useState({});
  const [expandedAskiEx, setExpandedAskiEx] = useState({});
  const [exDbPage, setExDbPage] = useState(1);
  const [askiExPage, setAskiExPage] = useState(1);
  const [periodCompare, setPeriodCompare] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [p1Start, setP1Start] = useState('');
  const [p1End, setP1End] = useState('');
  const [p2Start, setP2Start] = useState('');
  const [p2End, setP2End] = useState('');
  const [pIlce, setPIlce] = useState('');
  const [aboneHistories, setAboneHistories] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});
  const PAGE_SIZE = 50;

  const fetchAboneHistory = async (aboneno) => {
    if (!aboneno || aboneHistories[aboneno]) return;
    setHistoryLoading(prev => ({ ...prev, [aboneno]: true }));
    try {
      const res = await fetch(`${API}/api/aski/analysis/query`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aboneno }),
      }).then(r => r.json());
      const rows = (res.rows || []).sort((a, b) => (a.donem || 0) - (b.donem || 0));
      setAboneHistories(prev => ({ ...prev, [aboneno]: rows }));
    } catch (e) { console.warn('Abone gecmisi alinamadi:', e); }
    finally { setHistoryLoading(prev => ({ ...prev, [aboneno]: false })); }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dbStatusRaw, dbParks, askiPeriods] = await Promise.all([
        fetch(`${API}/api/aski/db-status`).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/park-verification/db-parks?limit=9999`).then(r => r.json()).catch(() => ({ parks: [], total: 0 })),
        fetch(`${API}/api/aski/analysis/periods`).then(r => r.json()).catch(() => ({ periods: [] })),
      ]);
      const dbStatus = dbStatusRaw?.summary || dbStatusRaw || {};

      let askiParks = [];
      try {
        const askiRaw = await fetch(`${API}/api/aski/analysis/query`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupBy: 'abone' }),
        }).then(r => r.json());
        askiParks = askiRaw.rows || [];
      } catch (e) { console.warn('ASKI park listesi alinamadi'); }

      let excelData = null;
      try {
        const raw = await readExcelFromBase64();
        const sheetNames = Object.keys(raw);
        const parkSheet = raw['PARK'] || [];
        const pasifSheet = raw['PASİF'] || raw['PASIF'] || [];
        const adresSheet = raw['ADRES LİSTESİ'] || raw['ADRES LISTESI'] || [];
        const excelParks = [];
        parkSheet.forEach((row, idx) => {
          if (idx < 5) return;
          const sira = row[0], adi = String(row[1] || '').trim(), adres = String(row[2] || '').trim(), mahalle = String(row[3] || '').trim();
          const cimAlan = parseFloat(row[4]) || null, sertZemin = parseFloat(row[5]) || null;
          const ioa = parseFloat(row[6]) || null, ciceklik = parseFloat(row[7]) || null;
          const yurumeYollari = parseFloat(row[8]) || null, pasifAlan = parseFloat(row[9]) || null;
          if (sira && !isNaN(sira) && adi.length > 2) excelParks.push({ sira, adi, adres, mahalle, cimAlan, sertZemin, ioa, ciceklik, yurumeYollari, pasifAlan });
        });
        const pasifAreas = [];
        pasifSheet.forEach((row, idx) => {
          if (idx < 3) return;
          const s = row[0], name = String(row[1] || '').trim();
          if (s && !isNaN(s) && name.length > 2) pasifAreas.push({ sira: s, adi: name, mahalle: String(row[3] || '').trim() });
        });
        excelData = { sheetNames, parkCount: excelParks.length, parks: excelParks, pasifCount: pasifAreas.length, pasifAreas, adresRows: adresSheet.length };
      } catch (exErr) { console.warn('Excel okunamadi:', exErr.message); }

      const comparison = buildComparison(askiParks, dbParks?.parks || [], excelData?.parks || []);

      setData({ dbStatus, dbParks, askiPeriods, excelData, askiParks, comparison, dbStatusRaw });
    } catch (e) {
      setError('Veri yuklenemedi: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const runPeriodCompare = async () => {
    if (!p1Start || !p1End || !p2Start || !p2End) return;
    setPeriodLoading(true);
    try {
      const body = { period1Start: p1Start, period1End: p1End, period2Start: p2Start, period2End: p2End };
      if (pIlce) body.ilce = pIlce;
      const res = await fetch(`${API}/api/aski/analysis/compare`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(r => r.json());
      setPeriodCompare(res);
    } catch (e) { console.error('Donem karsilastirma hatasi:', e); }
    finally { setPeriodLoading(false); }
  };

  const fmtPeriod = (p) => { const s = String(p); return s.length >= 6 ? s.substring(0,4) + '/' + s.substring(4,6) : s; };

  // norm, sigWords, buildComparison => imported from utils/parkComparison

  const generatePDF = async () => {
    if (!data) return;
    setGenerating(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210, H = 297;
      const now = new Date();
      const dateStr = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const blue = [0, 75, 145], darkBlue = [0, 50, 100], gray = [100, 116, 139];
      const dark = [30, 41, 59], white = [255, 255, 255], green = [16, 185, 129];
      const amber = [217, 119, 6], red = [220, 38, 38];

      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      const logoOk = await new Promise(r => { logoImg.onload = () => r(true); logoImg.onerror = () => r(false); logoImg.src = `${window.location.origin}/logo.png`; });

      const drawHeader = (title) => {
        doc.setFillColor(245, 249, 255); doc.rect(0, 0, W, 20, 'F');
        doc.setDrawColor(200, 215, 235); doc.setLineWidth(0.3); doc.line(0, 20, W, 20);
        if (logoOk) doc.addImage(logoImg, 'PNG', 8, 2, 32, 16);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...gray);
        doc.text('Pronuve - Veri Envanteri Raporu', 44, 11);
        doc.setFillColor(...blue); doc.rect(0, 24, W, 12, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...white);
        doc.text(tr(title), 15, 32);
      };

      const drawFooter = () => {
        const tp = doc.internal.getNumberOfPages();
        for (let p = 1; p <= tp; p++) {
          doc.setPage(p);
          const fy = H - 20;
          doc.setDrawColor(200, 215, 235); doc.setLineWidth(0.3); doc.line(15, fy, W - 15, fy);
          doc.setFillColor(...blue); doc.circle(20, fy + 6, 1.5, 'F');
          doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...gray);
          doc.text('www.pronuve.com', 24, fy + 8);
          doc.text('Olusturulma: ' + dateStr, 85, fy + 8);
          doc.text('info@pronuve.com', W - 15, fy + 8, { align: 'right' });
          if (p > 1) doc.text('Sayfa ' + (p - 1) + ' / ' + (tp - 1), 105, fy + 13, { align: 'center' });
        }
      };

      const ds = data.dbStatus || {};
      const dbParks = data.dbParks?.parks || [];
      const periods = data.askiPeriods?.periods || [];
      const askiTotal = ds.total || ds.total_records || 0;
      const dbParkCount = dbParks.length;
      const ex = data.excelData || {};
      const exParks = ex.parks || [];
      const exPasif = ex.pasifAreas || [];

      // KAPAK
      doc.setFillColor(245, 249, 255); doc.rect(0, 0, W, H, 'F');
      const drawWave = (yy, hh, op) => {
        doc.setFillColor(200, 225, 250); doc.setGState(new doc.GState({ opacity: op }));
        const steps = 40, sw = W / steps;
        for (let i = 0; i <= steps; i++) { const x = i * sw; const wy = yy + Math.sin(i * 0.4) * 4 + Math.cos(i * 0.2) * 3; doc.rect(x, wy, sw + 0.5, hh, 'F'); }
        doc.setGState(new doc.GState({ opacity: 1 }));
      };
      drawWave(30, 60, 0.15); drawWave(180, 80, 0.12); drawWave(230, 67, 0.08);
      if (logoOk) doc.addImage(logoImg, 'PNG', 15, 12, 50, 25);
      doc.setFillColor(...white); doc.setGState(new doc.GState({ opacity: 0.95 }));
      doc.roundedRect(25, 85, 160, 105, 8, 8, 'F'); doc.setGState(new doc.GState({ opacity: 1 }));
      if (logoOk) doc.addImage(logoImg, 'PNG', 75, 90, 60, 30);
      doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.setTextColor(...darkBlue);
      doc.text('PRONUVE', 105, 135, { align: 'center' });
      doc.text('VERI ENVANTERI RAPORU', 105, 147, { align: 'center' });
      doc.setFont('helvetica', 'italic'); doc.setFontSize(11); doc.setTextColor(...gray);
      doc.text('Mevcut Veri Kaynaklari ve Analiz Potansiyeli', 105, 160, { align: 'center' });
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text(dateStr, 105, 172, { align: 'center' });
      doc.text('Ankara - Su Yonetim Platformu', 105, 180, { align: 'center' });
      // SAYFA 2: VERI KAYNAKLARI OZET
      doc.addPage(); drawHeader('1. VERI KAYNAKLARI OZETI');
      let y = 46;
      const cards = [
        { label: 'ASKI Canli Veri', value: fmtNum(askiTotal) + ' kayit', sub: periods.length + ' donem', color: blue },
        { label: 'DB Parklar (KMZ/XLS)', value: dbParkCount + ' park', sub: 'water_billing.park', color: green },
        { label: 'Excel (Cankaya)', value: exParks.length + ' park', sub: '4 sheet / ' + (exPasif.length) + ' pasif', color: amber },
        { label: 'GeoJSON', value: '2 dosya', sub: 'Polygon sinir verisi', color: [99, 102, 241] },
      ];
      const cw = 42, cg = 4, sx = 15;
      cards.forEach((c, i) => {
        const cx = sx + i * (cw + cg);
        doc.setFillColor(250, 252, 255); doc.setDrawColor(220, 230, 245); doc.setLineWidth(0.3);
        doc.roundedRect(cx, y, cw, 32, 3, 3, 'FD');
        doc.setFillColor(...c.color); doc.circle(cx + 8, y + 10, 4.5, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...white);
        doc.text(c.label.charAt(0), cx + 8, y + 11.5, { align: 'center' });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...gray);
        doc.text(tr(c.label), cx + 15, y + 9);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...c.color);
        doc.text(tr(c.value), cx + 15, y + 19);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...gray);
        doc.text(tr(c.sub), cx + 15, y + 26);
      });
      y += 42;

      // Veri kaynaklari tablosu
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...darkBlue);
      doc.text('Veri Kaynaklari Detay', 15, y); y += 6;
      const srcCols = [45, 55, 40, 35];
      const srcH = ['Kaynak', 'Icerik', 'Kapsam', 'Durum'];
      doc.setFillColor(...blue); doc.rect(15, y, srcCols.reduce((a,b)=>a+b,0), 8, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...white);
      let hx = 15; srcH.forEach((h,i) => { doc.text(h, hx+2, y+5.5); hx += srcCols[i]; }); y += 8;
      const sources = [
        ['ASKI SOAP API', 'Su tuketim, abone bilgisi', 'Tum Ankara (' + periods.length + ' donem)', 'Aktif - Canli'],
        ['PostgreSQL DB', 'Park geometri, alan, sulama', dbParkCount + ' park kaydi', 'Aktif'],
        ['KMZ Dosyalari', 'Park sinirlari (polygon)', 'Cankaya bolgeleri', 'Yuklendi'],
        ['Excel (2025.xlsx)', exParks.length + ' park, ' + exPasif.length + ' pasif', 'Cankaya - 4 sheet', 'Aktif'],
        ['GeoJSON', 'Park polygon + alan verileri', '2 ornek dosya', 'Test'],
      ];
      sources.forEach((row, idx) => {
        if (idx % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(15, y, srcCols.reduce((a,b)=>a+b,0), 7, 'F'); }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...dark);
        let rx = 15; row.forEach((v,i) => { doc.text(tr(v), rx+2, y+5); rx += srcCols[i]; }); y += 7;
      });
      y += 10;

      // DB tablolari
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...darkBlue);
      doc.text('Veritabani Tablolari', 15, y); y += 6;
      const tbCols = [60, 55, 60];
      const tbH = ['Tablo', 'Icerik', 'Notlar'];
      doc.setFillColor(...green); doc.rect(15, y, tbCols.reduce((a,b)=>a+b,0), 8, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...white);
      hx = 15; tbH.forEach((h,i) => { doc.text(h, hx+2, y+5.5); hx += tbCols[i]; }); y += 8;
      const tables = [
        ['water_billing.park', 'Park bilgileri (KMZ+XLS)', 'name_kmz, name_xls, lat/lng'],
        ['water_billing.park_area', 'Park alan bilgileri', 'Cim, sert zemin, agaclik'],
        ['water_billing.park_irrigation', 'Sulama sistemleri', 'Tur, kapasite'],
        ['water_billing.subscriber', 'Su aboneleri', 'Abone no, sayac'],
        ['water_billing.water_meter', 'Sayac bilgileri', 'Sayac no, cap'],
        ['water_billing.park_surface_breakdown', 'Yuzey kirilimlari', 'Detayli alan verileri'],
        ['public.aski_aboneparklar', 'ASKI canli veri', fmtNum(askiTotal) + ' kayit'],
        ['public.park_verification', 'Park eslestirme', 'ASKI <-> DB eslesme'],
      ];
      tables.forEach((row, idx) => {
        if (idx % 2 === 0) { doc.setFillColor(240, 253, 244); doc.rect(15, y, tbCols.reduce((a,b)=>a+b,0), 7, 'F'); }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...dark);
        let rx = 15; row.forEach((v,i) => { doc.text(tr(v), rx+2, y+5); rx += tbCols[i]; }); y += 7;
      });
      // SAYFA 3: DB PARK LISTESI
      doc.addPage(); drawHeader('2. VERITABANI PARK LISTESI (KMZ / Excel)');
      y = 42;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text('water_billing.park tablosundaki tum parklar (' + dbParkCount + ' adet)', 15, y); y += 8;
      const pkCols = [10, 20, 65, 65, 20];
      const pkH = ['#', 'Park ID', 'KMZ Ismi', 'XLS Ismi', 'Koord.'];
      const drawPkHeader = () => {
        doc.setFillColor(...blue); doc.rect(15, y, pkCols.reduce((a,b)=>a+b,0), 8, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...white);
        let hx2 = 15; pkH.forEach((h,i) => { doc.text(h, hx2+1, y+5.5); hx2 += pkCols[i]; }); y += 8;
      };
      drawPkHeader();
      dbParks.forEach((p, i) => {
        if (y > H - 30) { doc.addPage(); drawHeader('2. VERITABANI PARK LISTESI (devam)'); y = 42; drawPkHeader(); }
        if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(15, y, pkCols.reduce((a,b)=>a+b,0), 6, 'F'); }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(...dark);
        let rx = 15;
        const hasCoord = p.latitude && p.longitude ? 'Var' : '-';
        const vals = [String(i+1), String(p.park_id||''), tr(String(p.name_kmz||'-').substring(0,32)), tr(String(p.name_xls||'-').substring(0,32)), hasCoord];
        vals.forEach((v,ci) => { doc.text(v, rx+1, y+4); rx += pkCols[ci]; }); y += 6;
      });
      // SAYFA 4: EXCEL CANKAYA PARK LISTESI
      doc.addPage(); drawHeader('3. EXCEL - CANKAYA PARK LISTESI (' + exParks.length + ' Park)');
      y = 42;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text('Kaynak: 2025.xlsx / PARK sheet - Cankaya Belediyesi Park ve Bahceler Mudurlugu', 15, y); y += 8;
      const exCols = [10, 60, 55, 40];
      const exH = ['#', 'Park Adi', 'Adres', 'Mahalle'];
      const drawExHeader = () => {
        doc.setFillColor(...green); doc.rect(15, y, exCols.reduce((a,b)=>a+b,0), 8, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...white);
        let hx2 = 15; exH.forEach((h,i) => { doc.text(h, hx2+1, y+5.5); hx2 += exCols[i]; }); y += 8;
      };
      drawExHeader();
      exParks.forEach((p, i) => {
        if (y > H - 30) { doc.addPage(); drawHeader('3. EXCEL - CANKAYA PARK LISTESI (devam)'); y = 42; drawExHeader(); }
        if (i % 2 === 0) { doc.setFillColor(240, 253, 244); doc.rect(15, y, exCols.reduce((a,b)=>a+b,0), 6, 'F'); }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(...dark);
        let rx = 15;
        const vals = [String(p.sira||i+1), tr(String(p.adi||'').substring(0,30)), tr(String(p.adres||'').substring(0,28)), tr(String(p.mahalle||'').substring(0,20))];
        vals.forEach((v,ci) => { doc.text(v, rx+1, y+4); rx += exCols[ci]; }); y += 6;
      });

      // SAYFA 5: PASIF ALANLAR
      if (exPasif.length > 0) {
        doc.addPage(); drawHeader('4. PASIF YESIL ALANLAR (' + exPasif.length + ' Alan)');
        y = 42;
        const paCols = [10, 80, 75];
        const paH = ['#', 'Alan Adi', 'Mahalle'];
        const drawPaHeader = () => {
          doc.setFillColor(...amber); doc.rect(15, y, paCols.reduce((a,b)=>a+b,0), 8, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...white);
          let hx2 = 15; paH.forEach((h,i) => { doc.text(h, hx2+1, y+5.5); hx2 += paCols[i]; }); y += 8;
        };
        drawPaHeader();
        exPasif.forEach((p, i) => {
          if (y > H - 30) { doc.addPage(); drawHeader('4. PASIF YESIL ALANLAR (devam)'); y = 42; drawPaHeader(); }
          if (i % 2 === 0) { doc.setFillColor(255, 251, 235); doc.rect(15, y, paCols.reduce((a,b)=>a+b,0), 6, 'F'); }
          doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(...dark);
          let rx = 15;
          const vals = [String(p.sira||i+1), tr(String(p.adi||'').substring(0,40)), tr(String(p.mahalle||'').substring(0,35))];
          vals.forEach((v,ci) => { doc.text(v, rx+1, y+4); rx += paCols[ci]; }); y += 6;
        });
      }
      // SAYFA SON: ANALIZ POTANSIYELI
      doc.addPage(); drawHeader('DONEMSEL TUKETIM KARSILASTIRMASI');
      y = 42;

      if (periodCompare) {
        const pc = periodCompare;
        doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...darkBlue);
        doc.text('Donem Bazli Su Tuketim Karsilastirmasi', 15, y); y += 8;

        const p1Total = fmtNum(pc.period1?.total || 0);
        const p2Total = fmtNum(pc.period2?.total || 0);
        const changeVal = pc.change != null ? parseFloat(pc.change) : null;

        const pcCards = [
          { label: 'Donem 1', value: tr(p1Total) + ' m3', sub: (pc.period1?.rows?.length || 0) + ' ay', color: blue },
          { label: 'Donem 2', value: tr(p2Total) + ' m3', sub: (pc.period2?.rows?.length || 0) + ' ay', color: amber },
        ];
        pcCards.forEach((c, i) => {
          const cx = 15 + i * 90;
          doc.setFillColor(250, 252, 255); doc.setDrawColor(...c.color); doc.setLineWidth(0.5);
          doc.roundedRect(cx, y, 80, 22, 3, 3, 'FD');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...c.color);
          doc.text(tr(c.label), cx + 5, y + 8);
          doc.setFontSize(12); doc.text(c.value, cx + 5, y + 16);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...gray);
          doc.text(c.sub, cx + 5, y + 20);
        });
        y += 30;

        if (changeVal != null) {
          const chColor = changeVal > 0 ? red : green;
          doc.setFillColor(...(changeVal > 0 ? [254, 242, 242] : [236, 253, 245]));
          doc.roundedRect(15, y, 80, 14, 3, 3, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...chColor);
          doc.text((changeVal > 0 ? '+' : '') + changeVal + '%', 20, y + 10);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...gray);
          doc.text('Degisim Orani', 55, y + 10);
          y += 20;
        }

        const pcRows = [...(pc.period1?.rows || []), ...(pc.period2?.rows || [])];
        if (pcRows.length > 0) {
          doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...darkBlue);
          doc.text('Aylik Tuketim Detayi', 15, y); y += 6;
          const pcCols = [30, 40, 35, 35, 35];
          doc.setFillColor(...blue); doc.rect(15, y, pcCols.reduce((a,b)=>a+b,0), 7, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...white);
          let pchx = 15; ['Donem', 'Toplam (m3)', 'Abone Sayisi', 'Ortalama', 'Grup'].forEach((h,i) => { doc.text(h, pchx+2, y+5); pchx += pcCols[i]; }); y += 7;

          const p1Set = new Set((pc.period1?.rows || []).map(r => String(r.donem)));
          pcRows.forEach((r, idx) => {
            if (y > H - 30) { doc.addPage(); drawHeader('DONEMSEL TUKETIM (devam)'); y = 42; }
            const grp = p1Set.has(String(r.donem)) ? 'D1' : 'D2';
            if (idx % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(15, y, pcCols.reduce((a,b)=>a+b,0), 6, 'F'); }
            doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...dark);
            let prx = 15;
            [fmtPeriod(r.donem), tr(fmtNum(r.toplam)), String(r.abone_sayisi || ''), tr(fmtNum(r.ortalama)), grp].forEach((v,i) => { doc.text(String(v), prx+2, y+4); prx += pcCols[i]; }); y += 6;
          });
        }
      } else {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...gray);
        doc.text('Donem karsilastirmasi henuz yapilmamis. Raporu olusturmadan once', 15, y);
        doc.text('"Karsilastir" butonunu kullanarak donem karsilastirmasi yapin.', 15, y + 6);
      }

      doc.addPage(); drawHeader('VERI KAYNAK KARSILASTIRMASI VE ESLESTIRME');
      y = 42;
      const cmp = data.comparison || {};

      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...darkBlue);
      doc.text('Kapsam Karsilastirmasi', 15, y); y += 8;

      const scopeItems = [
        ['ASKI Canli Veri', cmp.askiTotal + ' abone', 'Tum Ankara - ' + Object.keys(cmp.askiIlceler || {}).length + ' ilce', blue],
        ['DB Parklar (KMZ/XLS)', cmp.dbTotal + ' park', 'Cankaya bolgesi', green],
        ['Excel (2025.xlsx)', cmp.exTotal + ' park', 'Cankaya Belediyesi', amber],
      ];
      scopeItems.forEach((item, i) => {
        const bx = 15 + i * 60;
        doc.setFillColor(248, 250, 252); doc.setDrawColor(...item[3]); doc.setLineWidth(0.5);
        doc.roundedRect(bx, y, 55, 22, 2, 2, 'FD');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...item[3]);
        doc.text(tr(item[0]), bx + 3, y + 7);
        doc.setFontSize(11); doc.text(tr(item[1]), bx + 3, y + 14);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...gray);
        doc.text(tr(item[2]), bx + 3, y + 19);
      });
      y += 30;

      // ASKI ilce dagilimi
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...darkBlue);
      doc.text('ASKI Ilce Dagilimi', 15, y); y += 6;
      const ilceEntries = Object.entries(cmp.askiIlceler || {}).sort((a,b) => b[1] - a[1]);
      const ilceCols = [55, 30, 30];
      doc.setFillColor(...blue); doc.rect(15, y, ilceCols.reduce((a,b)=>a+b,0), 7, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...white);
      let ihx = 15; ['Ilce', 'Abone Sayisi', 'Oran'].forEach((h,i) => { doc.text(h, ihx+2, y+5); ihx += ilceCols[i]; }); y += 7;
      ilceEntries.forEach(([ilce, cnt], idx) => {
        if (y > H - 40) { doc.addPage(); drawHeader('VERI KAYNAK KARSILASTIRMASI (devam)'); y = 42; }
        if (idx % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(15, y, ilceCols.reduce((a,b)=>a+b,0), 6, 'F'); }
        const pct = cmp.askiTotal > 0 ? ((cnt / cmp.askiTotal) * 100).toFixed(1) : '0';
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...dark);
        const isCankaya = ilce.toLowerCase().includes('çankaya') || ilce.toLowerCase().includes('cankaya');
        if (isCankaya) { doc.setFont('helvetica', 'bold'); doc.setTextColor(...green); }
        let irx = 15;
        [tr(ilce), String(cnt), pct + '%'].forEach((v,i) => { doc.text(v, irx+2, y+4); irx += ilceCols[i]; }); y += 6;
      });
      y += 8;

      // Eslestirme ozet
      if (y > H - 80) { doc.addPage(); drawHeader('ESLESTIRME SONUCLARI'); y = 42; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...darkBlue);
      doc.text('Eslestirme Sonuclari', 15, y); y += 8;

      const matchSummary = [
        ['Excel <-> DB', String(cmp.exDbExact || 0), String(cmp.exDbPartial || 0), String((cmp.exTotal || 0) - (cmp.exDbExact || 0) - (cmp.exDbPartial || 0))],
        ['ASKI (Cankaya) <-> Excel', String(cmp.askiExExact || 0), String(cmp.askiExPartial || 0), String((cmp.askiCankayaCount || 0) - (cmp.askiExExact || 0) - (cmp.askiExPartial || 0))],
      ];
      const msCols = [55, 30, 30, 30];
      doc.setFillColor(30, 41, 59); doc.rect(15, y, msCols.reduce((a,b)=>a+b,0), 7, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...white);
      let mhx = 15; ['Kaynak Cifti', 'Tam Eslesme', 'Benzer', 'Eslesmez'].forEach((h,i) => { doc.text(h, mhx+2, y+5); mhx += msCols[i]; }); y += 7;
      matchSummary.forEach((row, idx) => {
        if (idx % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(15, y, msCols.reduce((a,b)=>a+b,0), 6, 'F'); }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...dark);
        let mrx = 15; row.forEach((v,i) => { doc.text(v, mrx+2, y+4); mrx += msCols[i]; }); y += 6;
      });
      y += 8;

      // Ornek eslesmeler (Excel<->DB)
      if ((cmp.exDbMatches || []).length > 0) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...green);
        doc.text('Ornek Eslesmeler: Excel <-> DB (ilk 15)', 15, y); y += 6;
        const emCols = [65, 65, 25];
        doc.setFillColor(...green); doc.rect(15, y, emCols.reduce((a,b)=>a+b,0), 7, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...white);
        let emhx = 15; ['Excel Park', 'DB Park', 'Tur'].forEach((h,i) => { doc.text(h, emhx+2, y+5); emhx += emCols[i]; }); y += 7;
        cmp.exDbMatches.slice(0, 15).forEach((m, idx) => {
          if (y > H - 30) { doc.addPage(); drawHeader('ESLESTIRME SONUCLARI (devam)'); y = 42; }
          if (idx % 2 === 0) { doc.setFillColor(240, 253, 244); doc.rect(15, y, emCols.reduce((a,b)=>a+b,0), 6, 'F'); }
          doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(...dark);
          let emrx = 15;
          [tr(String(m.excel||'').substring(0,32)), tr(String(m.db||'').substring(0,32)), m.type === 'tam' ? 'Tam' : 'Benzer'].forEach((v,i) => { doc.text(v, emrx+2, y+4); emrx += emCols[i]; }); y += 6;
        });
        y += 6;
      }

      // Onemli bilgi notu
      if (y > H - 50) { doc.addPage(); drawHeader('ONEMLI NOTLAR'); y = 42; }
      doc.setFillColor(255, 251, 235); doc.setDrawColor(251, 191, 36); doc.setLineWidth(0.5);
      doc.roundedRect(15, y, 180, 28, 3, 3, 'FD');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...amber);
      doc.text('Onemli Notlar:', 19, y + 7);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(100, 70, 10);
      doc.text('- ASKI verileri tum Ankara\'yi kapsar. Excel/KMZ sadece Cankaya ilcesine aittir.', 19, y + 14);
      doc.text('- ASKI abone isimleri kurum formati icerir (orn: "ANK.B.B.B.CEV.KOR..."), park isimleriyle farklidir.', 19, y + 20);
      doc.text('- Diger ilceler icin KMZ/Excel verileri saglandiginda kapsam genisleyecektir.', 19, y + 26);
      y += 36;

      // ANALIZ POTANSIYELI
      doc.addPage(); drawHeader('ANALIZ POTANSIYELI VE GELECEK ADIMLAR');
      y = 42;
      const sections = [
        { title: 'Mevcut Analiz Yetenekleri', color: green, items: [
          'Aylik/donemsel su tuketim raporlari (ASKI canli veri)',
          'Ilce bazli tuketim karsilastirmalari',
          'Park bazli tuketim siralamasi (en cok/en az)',
          'Donemler arasi tuketim trend analizi',
          'KMZ ile park sinirlari goruntuleme',
          'Excel verisi ile Cankaya park detaylari',
        ]},
        { title: 'Eslestirme Durumu', color: amber, items: [
          'ASKI verileri: Tum Ankara (tum ilceler) - ' + fmtNum(askiTotal) + ' kayit / ' + (cmp.askiTotal || 0) + ' abone',
          'ASKI Cankaya abonesi: ' + (cmp.askiCankayaCount || 0) + ' adet',
          'Excel/KMZ verisi: Cankaya ilcesi - ' + exParks.length + ' aktif park + ' + exPasif.length + ' pasif alan',
          'DB parklar: ' + dbParkCount + ' park (KMZ + XLS kaynakli)',
          'Excel <-> DB: ' + (cmp.exDbExact || 0) + ' tam + ' + (cmp.exDbPartial || 0) + ' benzer eslesme',
          'ASKI (Cankaya) <-> Excel: ' + (cmp.askiExExact || 0) + ' tam + ' + (cmp.askiExPartial || 0) + ' benzer eslesme',
          'ASKI abone isimleri ile DB park isimleri format farki nedeniyle dusuk eslestirme',
          'Manuel eslestirme (Park Dogrulama modulu) ile iyilestirilebilir',
        ]},
        { title: 'Eksik Veriler / Ihtiyaclar', color: red, items: [
          'Diger ilceler icin park geometri verileri (KMZ/Excel)',
          'Park alan bilgileri (cim, sert zemin) - tum ilceler',
          'Sulama sistemi detaylari - tum ilceler',
          'Meteoroloji verileri (yagis, sicaklik) entegrasyonu',
          'NDVI (uydu) verileri ile yesil alan izleme',
        ]},
        { title: 'Gelecek Potansiyel', color: blue, items: [
          'Anomali tespiti: Asiri tuketim uyarilari',
          'Verimlilik analizi: m3/m2 bazli park karsilastirmasi',
          'Tahminleme: Makine ogrenmesi ile tuketim tahmini',
          'Sulama optimizasyonu: Hava durumu + tuketim korelasyonu',
          'Dashboard: Gercek zamanli izleme ve raporlama',
        ]},
      ];
      sections.forEach(sec => {
        if (y > H - 60) { doc.addPage(); drawHeader('ANALIZ POTANSIYELI (devam)'); y = 42; }
        doc.setFillColor(...sec.color); doc.rect(15, y, 3, 10, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...sec.color);
        doc.text(tr(sec.title), 22, y + 7); y += 14;
        sec.items.forEach(item => {
          if (y > H - 30) { doc.addPage(); drawHeader('ANALIZ POTANSIYELI (devam)'); y = 42; }
          doc.setFillColor(...sec.color); doc.circle(19, y, 1.2, 'F');
          doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...dark);
          doc.text(tr(item), 23, y + 1.5); y += 7;
        });
        y += 6;
      });

      drawFooter();
      doc.save('Pronuve_Veri_Envanteri_' + dateStr.replace(/\./g, '') + '.pdf');
    } catch (e) {
      setError('PDF hatasi: ' + e.message);
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  const C = { bg: '#f8fafc', card: '#fff', border: '#e2e8f0', text: '#1e293b', textMuted: '#64748b', accent: '#2563eb', green: '#059669', greenBg: '#ecfdf5' };
  const containerStyle = { padding: '24px', maxWidth: 1100, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
  const cardStyle = { background: C.card, border: '1px solid ' + C.border, borderRadius: 8, padding: '20px 24px', marginBottom: 16 };
  const btnPrimary = { padding: '10px 24px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer' };
  const btnGreen = { padding: '10px 24px', background: C.green, color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer' };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>Veri Envanteri Raporu</h2>
        <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 16px' }}>
          Tum veri kaynaklarinizi (ASKI, KMZ, Excel, DB) iceren kapsamli envanter raporu olusturun. Sunum icin PDF olarak indirin.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }} onClick={loadData} disabled={loading}>
            {loading ? 'Yukleniyor...' : 'Veri Kaynaklarini Tara'}
          </button>
          {data && (
            <button style={{ ...btnGreen, opacity: generating ? 0.6 : 1 }} onClick={generatePDF} disabled={generating}>
              {generating ? 'PDF Olusturuluyor...' : 'PDF Rapor Indir'}
            </button>
          )}
        </div>
      </div>
      {error && <div style={{ ...cardStyle, background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>{error}</div>}
      {data && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 12px' }}>Taranan Veri Ozeti</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'ASKI Kayit', value: fmtNum(data.dbStatus?.total || data.dbStatus?.total_records || 0), bg: '#dbeafe' },
              { label: 'DB Park', value: String(data.dbParks?.parks?.length || 0), bg: '#ecfdf5' },
              { label: 'Excel Park', value: String(data.excelData?.parkCount || 0), bg: '#fef3c7' },
              { label: 'Pasif Alan', value: String(data.excelData?.pasifCount || 0), bg: '#ede9fe' },
              { label: 'Donem', value: String(data.askiPeriods?.periods?.length || 0), bg: '#fffbeb' },
            ].map((s, i) => (
              <div key={i} style={{ flex: '1 1 140px', background: s.bg, borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{s.value}</div>
                <div style={{ fontSize: 12, color: C.textMuted }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.comparison && (() => {
        const cmp = data.comparison;
        const tblH = { display: 'grid', gridTemplateColumns: '1fr 1fr 80px', padding: '8px 12px', background: '#1e293b', color: '#fff', fontSize: 12, fontWeight: 600, borderRadius: '6px 6px 0 0' };
        const tblR = (even) => ({ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', padding: '6px 12px', fontSize: 12, color: C.text, background: even ? '#f8fafc' : '#fff', borderBottom: '1px solid #f1f5f9' });
        const badge = (type) => ({ display: 'inline-block', padding: '1px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: '#fff', background: type === 'tam' ? '#059669' : '#d97706' });
        return (
          <>
            {/* GENEL KAPSAM */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>Veri Kaynak Kapsam Karsilastirmasi</h3>
              <p style={{ fontSize: 12, color: C.textMuted, margin: '0 0 14px' }}>Farkli veri kaynaklarinin kapsadigi alanlar ve ortak park analizi</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
                <div style={{ background: '#eff6ff', borderRadius: 8, padding: 14, border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: 11, color: '#2563eb', fontWeight: 600, marginBottom: 4 }}>ASKI Canli Veri</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>{cmp.askiTotal} abone</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Tum Ankara - {Object.keys(cmp.askiIlceler).length} ilce</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Cankaya abonesi: {cmp.askiCankayaCount}</div>
                </div>
                <div style={{ background: '#ecfdf5', borderRadius: 8, padding: 14, border: '1px solid #a7f3d0' }}>
                  <div style={{ fontSize: 11, color: '#059669', fontWeight: 600, marginBottom: 4 }}>DB Parklar (KMZ/Excel)</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#047857' }}>{cmp.dbTotal} park</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Cankaya bolgesi</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>water_billing.park tablosu</div>
                </div>
                <div style={{ background: '#fef3c7', borderRadius: 8, padding: 14, border: '1px solid #fcd34d' }}>
                  <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginBottom: 4 }}>Excel (2025.xlsx)</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#92400e' }}>{cmp.exTotal} park</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Cankaya Belediyesi</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>+ {data.excelData?.pasifCount || 0} pasif yesil alan</div>
                </div>
              </div>

              {/* ILCE DAGILIMI */}
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: '0 0 8px' }}>ASKI Ilce Dagilimi ({Object.keys(cmp.askiIlceler).length} ilce)</h4>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Object.entries(cmp.askiIlceler).sort((a,b) => b[1] - a[1]).map(([ilce, cnt], i) => (
                    <span key={i} style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500, background: ilce.toLowerCase().includes('çankaya') || ilce.toLowerCase().includes('cankaya') ? '#ecfdf5' : '#f1f5f9', color: ilce.toLowerCase().includes('çankaya') || ilce.toLowerCase().includes('cankaya') ? '#059669' : '#475569', border: ilce.toLowerCase().includes('çankaya') || ilce.toLowerCase().includes('cankaya') ? '1px solid #a7f3d0' : '1px solid #e2e8f0' }}>
                      {ilce}: {cnt}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ background: '#fffbeb', borderRadius: 8, padding: 12, border: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
                <strong>Onemli:</strong> ASKI verileri tum Ankara'yi kapsar ({cmp.askiTotal} abone, {Object.keys(cmp.askiIlceler).length} ilce). Excel ve KMZ verileri yalnizca Cankaya ilcesine aittir. Bu nedenle dogrudan eslestirme sadece Cankaya aboneleri ({cmp.askiCankayaCount}) ile mumkundur.
              </div>
            </div>

            {/* EXCEL vs DB ESLESMESI */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>Excel - DB Park Eslesmesi</h3>
              <p style={{ fontSize: 12, color: C.textMuted, margin: '0 0 12px' }}>2025.xlsx parklari ile water_billing.park karsilastirmasi</p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: '1 1 120px', textAlign: 'center', background: '#ecfdf5', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#059669' }}>{cmp.exDbExact}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Tam Eslesme</div>
                </div>
                <div style={{ flex: '1 1 120px', textAlign: 'center', background: '#fef3c7', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#d97706' }}>{cmp.exDbPartial}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Benzer Eslesme</div>
                </div>
                <div style={{ flex: '1 1 120px', textAlign: 'center', background: '#fef2f2', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>{cmp.exTotal - cmp.exDbExact - cmp.exDbPartial}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Eslesmeyenler</div>
                </div>
              </div>
              {cmp.exDbMatches.length > 0 && (() => {
                const visible = cmp.exDbMatches.slice(0, exDbPage * PAGE_SIZE);
                const hasMore = cmp.exDbMatches.length > exDbPage * PAGE_SIZE;
                return (
                  <div style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <div style={tblH}><span>Excel Park</span><span>DB Park</span><span>Tur</span></div>
                    {visible.map((m, i) => (
                      <React.Fragment key={i}>
                        <div
                          style={{ ...tblR(i % 2 === 0), cursor: 'pointer', transition: 'background 0.15s' }}
                          onClick={() => {
                            const wasExpanded = expandedExDb[i];
                            setExpandedExDb(prev => ({ ...prev, [i]: !prev[i] }));
                            if (!wasExpanded && m.askiData?.aboneno) fetchAboneHistory(m.askiData.aboneno);
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#f8fafc' : '#fff'}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 10, color: '#94a3b8', transform: expandedExDb[i] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>&#9654;</span>
                            {m.excel}
                          </span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.db}</span>
                          <span><span style={badge(m.type)}>{m.type === 'tam' ? 'Tam' : 'Benzer'}</span></span>
                        </div>
                        {expandedExDb[i] && (
                          <div style={{ background: '#f0f4ff', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Excel Kaydi</div>
                              {[
                                ['Park Adi', m.excelData?.adi],
                                ['Adres', m.excelData?.adres],
                                ['Mahalle', m.excelData?.mahalle],
                                ['Sira No', m.excelData?.sira],
                                ['Cim Alan', m.excelData?.cimAlan ? fmtNum(m.excelData.cimAlan) + ' m2' : null],
                                ['Sert Zemin', m.excelData?.sertZemin ? fmtNum(m.excelData.sertZemin) + ' m2' : null],
                                ['C.O.A', m.excelData?.ioa ? fmtNum(m.excelData.ioa) + ' m2' : null],
                                ['Ciceklik', m.excelData?.ciceklik ? fmtNum(m.excelData.ciceklik) + ' m2' : null],
                                ['Yurume Yollari', m.excelData?.yurumeYollari ? fmtNum(m.excelData.yurumeYollari) + ' m2' : null],
                                ['Pasif Alan', m.excelData?.pasifAlan ? fmtNum(m.excelData.pasifAlan) + ' m2' : null],
                              ].map(([k, v]) => v ? (
                                <div key={k} style={{ fontSize: 11, marginBottom: 3 }}>
                                  <span style={{ color: '#64748b', fontWeight: 600 }}>{k}: </span>
                                  <span style={{ color: '#1e293b' }}>{v}</span>
                                </div>
                              ) : null)}
                            </div>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>DB Kaydi</div>
                              {[
                                ['Park ID', m.dbData?.park_id],
                                ['KMZ Ismi', m.dbData?.name_kmz],
                                ['XLS Ismi', m.dbData?.name_xls],
                                ['Enlem', m.dbData?.latitude],
                                ['Boylam', m.dbData?.longitude],
                                ['Koordinat', m.dbData?.latitude && m.dbData?.longitude ? 'Var' : 'Yok'],
                              ].map(([k, v]) => v ? (
                                <div key={k} style={{ fontSize: 11, marginBottom: 3 }}>
                                  <span style={{ color: '#64748b', fontWeight: 600 }}>{k}: </span>
                                  <span style={{ color: '#1e293b' }}>{v}</span>
                                </div>
                              ) : null)}
                            </div>
                            {m.ortak && (
                              <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#64748b' }}>
                                <span style={{ fontWeight: 600 }}>Ortak Kelimeler: </span>{m.ortak}
                              </div>
                            )}
                            {/* ASKI eşleşme bilgisi ve tüketim geçmişi */}
                            {m.askiData ? (
                              <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#2563eb', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>ASKI Eslesmesi - Tuketim Gecmisi</div>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8, fontSize: 11 }}>
                                  {[
                                    ['Abone No', m.askiData.aboneno],
                                    ['Abone Adi', m.askiData.abone_adi],
                                    ['Toplam', m.askiData.toplam_tuketim ? fmtNum(m.askiData.toplam_tuketim) + ' m3' : null],
                                    ['Ortalama', m.askiData.ort_tuketim ? fmtNum(m.askiData.ort_tuketim) + ' m3' : null],
                                    ['Donem', m.askiData.ilk_donem && m.askiData.son_donem ? `${m.askiData.ilk_donem} - ${m.askiData.son_donem}` : null],
                                  ].map(([k, v]) => v ? (
                                    <span key={k} style={{ background: '#eff6ff', padding: '2px 8px', borderRadius: 4, border: '1px solid #bfdbfe' }}>
                                      <span style={{ color: '#64748b', fontWeight: 600 }}>{k}: </span>
                                      <span style={{ color: '#1e40af' }}>{v}</span>
                                    </span>
                                  ) : null)}
                                </div>
                                {historyLoading[m.askiData.aboneno] ? (
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Yukleniyor...</div>
                                ) : aboneHistories[m.askiData.aboneno]?.length > 0 ? (
                                  <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                      <thead>
                                        <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                                          <th style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Donem</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Tuketim (m3)</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Tutar (TL)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {aboneHistories[m.askiData.aboneno].map((row, ri) => {
                                          const maxVal = Math.max(...aboneHistories[m.askiData.aboneno].map(r => r.m3_tuketim || 0));
                                          const pct = maxVal > 0 ? ((row.m3_tuketim || 0) / maxVal) * 100 : 0;
                                          return (
                                            <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                              <td style={{ padding: '3px 8px', color: '#334155' }}>{row.donem}</td>
                                              <td style={{ padding: '3px 8px', textAlign: 'right', position: 'relative' }}>
                                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: '#dbeafe', borderRadius: 2, zIndex: 0 }}></div>
                                                <span style={{ position: 'relative', zIndex: 1, fontWeight: 500, color: '#1e40af' }}>{fmtNum(row.m3_tuketim)}</span>
                                              </td>
                                              <td style={{ padding: '3px 8px', textAlign: 'right', color: '#475569' }}>{row.tutar ? fmtNum(row.tutar) : '-'}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Gecmis tuketim verisi bulunamadi</div>
                                )}
                              </div>
                            ) : (
                              <div style={{ gridColumn: '1 / -1', marginTop: 6, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                                Bu park icin ASKI eslesmesi bulunamadi
                              </div>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    {hasMore && (
                      <div style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <button onClick={() => setExDbPage(p => p + 1)} style={{ padding: '4px 16px', fontSize: 12, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                          Daha fazla goster ({cmp.exDbMatches.length - visible.length} kaldi)
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* ASKI vs EXCEL ESLESMESI */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>ASKI (Cankaya) - Excel Park Eslesmesi</h3>
              <p style={{ fontSize: 12, color: C.textMuted, margin: '0 0 12px' }}>ASKI Cankaya aboneleri ({cmp.askiCankayaCount}) ile Excel parklari ({cmp.exTotal}) karsilastirmasi</p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: '1 1 120px', textAlign: 'center', background: '#ecfdf5', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#059669' }}>{cmp.askiExExact}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Tam Eslesme</div>
                </div>
                <div style={{ flex: '1 1 120px', textAlign: 'center', background: '#fef3c7', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#d97706' }}>{cmp.askiExPartial}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Benzer Eslesme</div>
                </div>
                <div style={{ flex: '1 1 120px', textAlign: 'center', background: '#f1f5f9', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#475569' }}>{cmp.askiCankayaCount - cmp.askiExExact - cmp.askiExPartial}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Eslesmeyenler</div>
                </div>
              </div>
              {cmp.askiExMatches.length > 0 && (() => {
                const visible = cmp.askiExMatches.slice(0, askiExPage * PAGE_SIZE);
                const hasMore = cmp.askiExMatches.length > askiExPage * PAGE_SIZE;
                return (
                  <div style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', padding: '8px 12px', background: '#1e293b', color: '#fff', fontSize: 12, fontWeight: 600, borderRadius: '6px 6px 0 0' }}><span>ASKI Abone</span><span>Cikarilan / Excel</span><span>Tur</span></div>
                    {visible.map((m, i) => (
                      <React.Fragment key={i}>
                        <div
                          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', padding: '6px 12px', fontSize: 11, color: C.text, background: i % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' }}
                          onClick={() => {
                            const wasExpanded = expandedAskiEx[i];
                            setExpandedAskiEx(prev => ({ ...prev, [i]: !prev[i] }));
                            if (!wasExpanded && m.askiData?.aboneno) fetchAboneHistory(m.askiData.aboneno);
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#f8fafc' : '#fff'}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 10, color: '#94a3b8', transform: expandedAskiEx[i] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>&#9654;</span>
                            {m.extracted || m.aski}
                          </span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.excel || m.extracted}</span>
                          <span><span style={badge(m.type)}>{m.type === 'tam' ? 'Tam' : 'Benzer'}</span></span>
                        </div>
                        {expandedAskiEx[i] && (
                          <div style={{ background: '#f0f4ff', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#2563eb', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>ASKI Kaydi</div>
                              {[
                                ['Abone Adi', m.askiData?.abone_adi],
                                ['Cikarilan Isim', m.extracted],
                                ['Abone No', m.askiData?.aboneno],
                                ['Abone Tipi', m.askiData?.abone_tipi],
                                ['Abone Tipi (Ana Grup)', m.askiData?.abone_tipi_anagrup],
                                ['Ilce', m.askiData?.ilce_adi],
                                ['Mahalle', m.askiData?.mahalle_adi],
                                ['Sokak', m.askiData?.sokak_adi],
                                ['Kaynak', m.askiData?.kaynak],
                                ['Sayac Turu', m.askiData?.sayac_turu],
                                ['Sayac Capi', m.askiData?.sayac_capi ? m.askiData.sayac_capi + ' mm' : null],
                                ['Toplam Tuketim', m.askiData?.toplam_tuketim ? fmtNum(m.askiData.toplam_tuketim) + ' m3' : null],
                                ['Ortalama Tuketim', m.askiData?.ort_tuketim ? fmtNum(m.askiData.ort_tuketim) + ' m3' : null],
                                ['Min Tuketim', m.askiData?.min_tuketim ? fmtNum(m.askiData.min_tuketim) + ' m3' : null],
                                ['Max Tuketim', m.askiData?.max_tuketim ? fmtNum(m.askiData.max_tuketim) + ' m3' : null],
                                ['Donem Sayisi', m.askiData?.donem_sayisi],
                                ['Ilk Donem', m.askiData?.ilk_donem],
                                ['Son Donem', m.askiData?.son_donem],
                              ].map(([k, v]) => v ? (
                                <div key={k} style={{ fontSize: 11, marginBottom: 3 }}>
                                  <span style={{ color: '#64748b', fontWeight: 600 }}>{k}: </span>
                                  <span style={{ color: '#1e293b' }}>{v}</span>
                                </div>
                              ) : null)}
                            </div>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Excel Kaydi</div>
                              {m.excelData ? [
                                ['Park Adi', m.excelData.adi],
                                ['Adres', m.excelData.adres],
                                ['Mahalle', m.excelData.mahalle],
                                ['Sira No', m.excelData.sira],
                                ['Cim Alan', m.excelData.cimAlan ? fmtNum(m.excelData.cimAlan) + ' m2' : null],
                                ['Sert Zemin', m.excelData.sertZemin ? fmtNum(m.excelData.sertZemin) + ' m2' : null],
                                ['C.O.A', m.excelData.ioa ? fmtNum(m.excelData.ioa) + ' m2' : null],
                                ['Ciceklik', m.excelData.ciceklik ? fmtNum(m.excelData.ciceklik) + ' m2' : null],
                                ['Yurume Yollari', m.excelData.yurumeYollari ? fmtNum(m.excelData.yurumeYollari) + ' m2' : null],
                                ['Pasif Alan', m.excelData.pasifAlan ? fmtNum(m.excelData.pasifAlan) + ' m2' : null],
                              ].map(([k, v]) => v ? (
                                <div key={k} style={{ fontSize: 11, marginBottom: 3 }}>
                                  <span style={{ color: '#64748b', fontWeight: 600 }}>{k}: </span>
                                  <span style={{ color: '#1e293b' }}>{v}</span>
                                </div>
                              ) : null) : (
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>Excel verisi bulunamadi</div>
                              )}
                            </div>
                            {m.ortak && (
                              <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#64748b' }}>
                                <span style={{ fontWeight: 600 }}>Ortak Kelimeler: </span>{m.ortak}
                              </div>
                            )}
                            {/* Dönem bazlı tüketim geçmişi */}
                            {m.askiData?.aboneno && (
                              <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Donem Bazli Tuketim Gecmisi</div>
                                {historyLoading[m.askiData.aboneno] ? (
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Yukleniyor...</div>
                                ) : aboneHistories[m.askiData.aboneno]?.length > 0 ? (
                                  <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                      <thead>
                                        <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                                          <th style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Donem</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Tuketim (m3)</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Tutar (TL)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {aboneHistories[m.askiData.aboneno].map((row, ri) => {
                                          const maxVal = Math.max(...aboneHistories[m.askiData.aboneno].map(r => r.m3_tuketim || 0));
                                          const pct = maxVal > 0 ? ((row.m3_tuketim || 0) / maxVal) * 100 : 0;
                                          return (
                                            <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                              <td style={{ padding: '3px 8px', color: '#334155' }}>{row.donem}</td>
                                              <td style={{ padding: '3px 8px', textAlign: 'right', position: 'relative' }}>
                                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: '#dbeafe', borderRadius: 2, zIndex: 0 }}></div>
                                                <span style={{ position: 'relative', zIndex: 1, fontWeight: 500, color: '#1e40af' }}>{fmtNum(row.m3_tuketim)}</span>
                                              </td>
                                              <td style={{ padding: '3px 8px', textAlign: 'right', color: '#475569' }}>{row.tutar ? fmtNum(row.tutar) : '-'}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Gecmis tuketim verisi bulunamadi</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    {hasMore && (
                      <div style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <button onClick={() => setAskiExPage(p => p + 1)} style={{ padding: '4px 16px', fontSize: 12, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                          Daha fazla goster ({cmp.askiExMatches.length - visible.length} kaldi)
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* DONEMSEL TUKETIM KARSILASTIRMASI */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>Donemsel Su Tuketim Karsilastirmasi</h3>
              <p style={{ fontSize: 12, color: C.textMuted, margin: '0 0 14px' }}>Iki farkli donem araligini secip tuketim verilerini karsilastirin</p>
              {data?.askiPeriods?.periods?.length > 0 && (() => {
                const periods = data.askiPeriods.periods.map(p => String(typeof p === 'object' ? p.donem || p.period : p)).sort();
                const ilceler = data.askiPeriods.ilceler || Object.keys(data.comparison?.askiIlceler || {});
                const selStyle = { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: C.text, background: '#fff', minWidth: 100 };
                return (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
                      <div style={{ background: '#eff6ff', borderRadius: 8, padding: 14, border: '1px solid #bfdbfe' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', marginBottom: 8 }}>Donem 1</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <select style={selStyle} value={p1Start} onChange={e => setP1Start(e.target.value)}>
                            <option value="">Baslangic</option>
                            {periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                          </select>
                          <select style={selStyle} value={p1End} onChange={e => setP1End(e.target.value)}>
                            <option value="">Bitis</option>
                            {periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={{ background: '#fef3c7', borderRadius: 8, padding: 14, border: '1px solid #fcd34d' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#b45309', marginBottom: 8 }}>Donem 2</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <select style={selStyle} value={p2Start} onChange={e => setP2Start(e.target.value)}>
                            <option value="">Baslangic</option>
                            {periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                          </select>
                          <select style={selStyle} value={p2End} onChange={e => setP2End(e.target.value)}>
                            <option value="">Bitis</option>
                            {periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
                      {ilceler.length > 0 && (
                        <select style={selStyle} value={pIlce} onChange={e => setPIlce(e.target.value)}>
                          <option value="">Tum Ilceler</option>
                          {ilceler.map(il => <option key={il} value={il}>{il}</option>)}
                        </select>
                      )}
                      <button onClick={runPeriodCompare} disabled={periodLoading || !p1Start || !p1End || !p2Start || !p2End}
                        style={{ ...btnPrimary, opacity: (periodLoading || !p1Start || !p1End || !p2Start || !p2End) ? 0.5 : 1, padding: '8px 20px', fontSize: 13 }}>
                        {periodLoading ? 'Yukleniyor...' : 'Karsilastir'}
                      </button>
                    </div>

                    {periodCompare && (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, marginBottom: 16 }}>
                          <div style={{ background: '#eff6ff', borderRadius: 8, padding: 16, textAlign: 'center', border: '1px solid #bfdbfe' }}>
                            <div style={{ fontSize: 11, color: '#2563eb', fontWeight: 600, marginBottom: 4 }}>Donem 1: {fmtPeriod(p1Start)} - {fmtPeriod(p1End)}</div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e40af' }}>{fmtNum(periodCompare.period1?.total)} m3</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{periodCompare.period1?.rows?.length || 0} aylik veri</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {periodCompare.change != null && (() => {
                              const ch = parseFloat(periodCompare.change);
                              const isUp = ch > 0;
                              const color = isUp ? '#dc2626' : '#059669';
                              return (
                                <div style={{ textAlign: 'center', padding: '8px 16px', borderRadius: 8, background: isUp ? '#fef2f2' : '#ecfdf5', border: `1px solid ${isUp ? '#fecaca' : '#a7f3d0'}` }}>
                                  <div style={{ fontSize: 24, fontWeight: 700, color }}>{isUp ? '+' : ''}{ch}%</div>
                                  <div style={{ fontSize: 10, color: '#64748b' }}>Degisim</div>
                                </div>
                              );
                            })()}
                          </div>
                          <div style={{ background: '#fef3c7', borderRadius: 8, padding: 16, textAlign: 'center', border: '1px solid #fcd34d' }}>
                            <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginBottom: 4 }}>Donem 2: {fmtPeriod(p2Start)} - {fmtPeriod(p2End)}</div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#92400e' }}>{fmtNum(periodCompare.period2?.total)} m3</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{periodCompare.period2?.rows?.length || 0} aylik veri</div>
                          </div>
                        </div>

                        {/* Bar chart */}
                        {(() => {
                          const allRows = [...(periodCompare.period1?.rows || []), ...(periodCompare.period2?.rows || [])];
                          const maxVal = Math.max(...allRows.map(r => parseFloat(r.toplam || 0)), 1);
                          return (
                            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16, border: '1px solid #e2e8f0' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 12 }}>Aylik Tuketim Grafigi</div>
                              <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 120 }}>
                                {(periodCompare.period1?.rows || []).map((r, i) => (
                                  <div key={'p1-'+i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <div style={{ fontSize: 8, color: '#64748b' }}>{fmtNum(r.toplam)}</div>
                                    <div style={{ width: '100%', background: '#3b82f6', borderRadius: '3px 3px 0 0', height: Math.max(4, (parseFloat(r.toplam) / maxVal) * 100) }} />
                                    <div style={{ fontSize: 7, color: '#94a3b8', writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 30 }}>{fmtPeriod(r.donem)}</div>
                                  </div>
                                ))}
                                <div style={{ width: 2, background: '#cbd5e1', height: '100%', margin: '0 4px', flexShrink: 0 }} />
                                {(periodCompare.period2?.rows || []).map((r, i) => (
                                  <div key={'p2-'+i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <div style={{ fontSize: 8, color: '#64748b' }}>{fmtNum(r.toplam)}</div>
                                    <div style={{ width: '100%', background: '#f59e0b', borderRadius: '3px 3px 0 0', height: Math.max(4, (parseFloat(r.toplam) / maxVal) * 100) }} />
                                    <div style={{ fontSize: 7, color: '#94a3b8', writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 30 }}>{fmtPeriod(r.donem)}</div>
                                  </div>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8 }}>
                                <span style={{ fontSize: 10, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, background: '#3b82f6', borderRadius: 2, display: 'inline-block' }} /> Donem 1</span>
                                <span style={{ fontSize: 10, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, background: '#f59e0b', borderRadius: 2, display: 'inline-block' }} /> Donem 2</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })()}
              {(!data?.askiPeriods?.periods || data.askiPeriods.periods.length === 0) && (
                <div style={{ fontSize: 12, color: '#94a3b8', padding: 16, textAlign: 'center' }}>Donem verisi bulunamadi. Once "Veri Kaynaklarini Tara" butonuna basin.</div>
              )}
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default DataInventoryReport;
